import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Campaign } from '../../services/campaign.service';

interface Goal {
  id: string;
  name: string;
  event: string;
  achieved: number;
  target: number;
  timeframe: string;
  status: boolean;
}

/**
 * Displays campaign goals and their progress, transforming campaign goal data into view-friendly Goal objects and providing helpers for status, styling, progress calculation and navigation to the campaign editor.
 * @component
 * @selector app-campaign-goals
 * @example
 *   <app-campaign-goals [campaign]="sampleCampaign"></app-campaign-goals>
 * @input Campaign campaign - The Campaign object containing a goals array and metadata; required. The component derives a list of Goal view models from campaign.goals for presentation.
 * @output none - This component does not emit any output events; user interactions that require navigation are handled internally via the Router.
 *
 * Key methods:
 * - get goals(): Goal[] - Getter that maps campaign.goals into an array of Goal objects used by the template. Returns an empty array if campaign is not provided.
 * - getGoalStatus(achieved: number, target: number): 'on-track' | 'behind' | 'completed' | 'not-started' - Determines a goal's status: "completed" when achieved >= target, "not-started" when achieved === 0, "on-track" when progress >= 75%, otherwise "behind".
 * - getProgressPercentage(goal: Goal): number - Calculates and clamps the progress percentage for a goal to a maximum of 100.
 * - getStatusClass(status: string): string - Returns a CSS class name mapped to the given status for status badge styling.
 * - getStatusDisplay(status: string): string - Returns a human-readable label for a given status key.
 * - getProgressBarClass(status: string): string - Returns a CSS class name for progress bar styling based on status.
 * - onEditClick(): void - Navigates to the campaign editor's "Goals" step and includes the campaignId as a query parameter.
 * - removeUnderscores(value: string): string - Utility that replaces hyphens/dashes with spaces for display (returns an empty string for falsy input).
 *
 * Lifecycle hooks:
 * - No Angular lifecycle hooks (ngOnInit, ngOnChanges, etc.) are implemented in this component; it relies on the campaign @Input being provided and uses computed getters and methods to prepare data for the template.
 */
@Component({
  selector: 'app-campaign-goals',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './campaign-goals.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-goals.component.scss'],
})
export class CampaignGoalsComponent {
  @Input() campaign!: Campaign;

  constructor(private router: Router) {}

  // Generate goals data from campaign data
  /**
   * Build and return an array of Goal objects derived from the component's campaign data. Returns an empty array if no campaign is set.
   * @example
   * goals()
   * [{ id: 'g1', name: ' Sample Goal', event: 'purchase', achieved: 0, target: 100, timeframe: 'monthly', status: 'active' }]
   * @returns {Goal[]} Array of Goal objects created from this.campaign.goals.
   */
  get goals(): Goal[] {
    if (!this.campaign) return [];

    const goals: Goal[] = [];

    // Create goals based on campaign goals array data
    this.campaign.goals.forEach((goal, index) => {
      goals.push({
        id: goal.id,
        name: ` ${goal.goal_name}`,
        event: goal.event,
        achieved: 0,
        target: goal.target_value,
        timeframe: goal.timeframe,
        status: goal.status,
      });
    });

    return goals;
  }

  getGoalStatus(
    achieved: number,
    target: number
  ): 'on-track' | 'behind' | 'completed' | 'not-started' {
    if (achieved >= target) return 'completed';
    if (achieved === 0) return 'not-started';
    const progress = (achieved / target) * 100;
    return progress >= 75 ? 'on-track' : 'behind';
  }

  getProgressPercentage(goal: Goal): number {
    return Math.min((goal.achieved / goal.target) * 100, 100);
  }

  getStatusClass(status: string): string {
    const statusClassMap: Record<string, string> = {
      'on-track': 'status-on-track',
      behind: 'status-behind',
      completed: 'status-completed',
      'not-started': 'status-not-started',
    };
    return statusClassMap[status] || '';
  }

  getStatusDisplay(status: string): string {
    const statusMap: Record<string, string> = {
      'on-track': 'On Track',
      behind: 'Behind',
      completed: 'Completed',
      'not-started': 'Not Started',
    };
    return statusMap[status] || status;
  }

  getProgressBarClass(status: string): string {
    const progressClassMap: Record<string, string> = {
      'on-track': 'progress-on-track',
      behind: 'progress-behind',
      completed: 'progress-completed',
      'not-started': 'progress-not-started',
    };
    return progressClassMap[status] || '';
  }

  onEditClick(): void {
    this.router.navigate(['/campaign-editor/finish/', 'Goals'], {
      queryParams: { campaignId: this.campaign.id },
    });
  }

  removeUnderscores(value: string): string {
    return value ? value.replace(/-/g, ' ') : '';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
